import { useMemo, useState, useEffect } from "react";
import { toIsoOrNull, parseDateOrNull } from "@/lib/dates";
import { toast } from "@/lib/toast";
import {
  Plus, X, Paperclip, Users, Repeat, FileText, Target,
  Calendar as CalendarIcon, ListChecks, ListTodo,
  Bell, Trash2, ArrowLeft, Zap, RotateCw, MapPin, ChevronDown,
  GripVertical, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RecurrenceEditor from "./RecurrenceEditor";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import FormSection from "@/components/shared/FormSection";
import ModuleDialog from "@/components/shared/ModuleDialog";
import DurationPicker, { EMPTY_DURATION, toMinutes, formatDuration } from "@/components/shared/DurationPicker";
import { MODULE_COLORS } from "@/lib/moduleColors";
import PriorityBadge from "./PriorityBadge";
import AssigneeSelector from "./AssigneeSelector";
import { DeadlineInline } from "./DeadlineDisplay";
import Combobox from "@/components/settings/Combobox";
import FileOrScanInput from "@/components/common/FileOrScanInput";
import useUploadQueue from "@/hooks/useUploadQueue";
import { useAuth } from "@/context/AuthContext";
import api, { errText } from "@/lib/api";
import { useRequiredFields, requiredClass } from "@/lib/useRequiredFields";
import { FieldError, MissingSummary } from "@/components/common/RequiredField";
import { J3Groups, PANEL } from "@/components/common/ProjectJ3";

// Tint the section icons + the little primary + button with the module
// colour so the dialog reads as "Tâches" at a single glance.
const YELLOW = MODULE_COLORS.tasks;   // #F9BC26
const PLUS_GREEN = "#669D24";          // shared with Combobox internal + btn

// Reminder types — one colour + label per flavour so the list is scannable.
const REMINDER_META = {
  before: { color: "#0078D4", label: "Avant",  Icon: ArrowLeft, hint: "Rappel unique déclenché avant l'échéance (délai = somme des 5 compteurs)" },
  during: { color: "#FF8C00", label: "Pendant", Icon: Zap,       hint: "Rappel répété pendant l'exécution de la tâche, à la cadence choisie, jusqu'à ce qu'elle soit terminée" },
  after:  { color: "#EF4444", label: "Après",  Icon: RotateCw,  hint: "Rappel répété après l'échéance, à la cadence choisie, jusqu'à ce que la tâche soit terminée" },
};

const FREQS = [
  { v: "hourly", l: "Horaire" },
  { v: "daily", l: "Quotidien" },
  { v: "weekly", l: "Hebdomadaire" },
  { v: "monthly", l: "Mensuel" },
  { v: "quarterly", l: "Trimestriel" },
  { v: "custom", l: "Personnalisé (heures)" },
];

// Spec iter131 (Feb 2026) — 3 niveaux (« Faible / normal » retiré).
const PRIORITIES = ["moyen", "urgent", "immediat"];

/** Small red star used to flag mandatory field labels consistently. */
const Req = () => <span className="text-status-danger ml-0.5">*</span>;

export default function TaskCreateDialog({
  open,
  onOpenChange,
  projects,
  users,
  onCreated,
  onUpdated,
  initialExecutionDate = "",
  initialSubject = "",
  initialMessage = "",
  sourceAviId = "",
  initialProjectId = "",
  initialChecklist = [],
  linkedDocumentId = "",  // spec 2026-02-XX rev16 (point J)
  existingTask = null,       // spec 2026-02-18 point 4 — edit mode
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEditMode = !!existingTask;

  // ------- Local reference lists (grow when Combobox creates on-the-fly) ---
  const [projectList, setProjectList] = useState(projects || []);
  useEffect(() => { setProjectList(projects || []); }, [projects]);

  // ------- Form state ------------------------------------------------------
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [projectName, setProjectName] = useState("");   // Combobox is name-based
  const [executionDate, setExecutionDate] = useState(initialExecutionDate || "");
  const [deadline, setDeadline] = useState("");
  const [priority, setPriority] = useState("moyen");
  const [assignees, setAssignees] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [checkInput, setCheckInput] = useState("");
  // reminders: [{ id, at (ISO), type ("before"|"during"|"after"), interval_minutes? }]
  const [reminders, setReminders] = useState([]);
  const [remType, setRemType] = useState("before");   // active tab
  const [duration, setDuration] = useState({ ...EMPTY_DURATION });
  const attachQueue = useUploadQueue();
  const [recEnabled, setRecEnabled] = useState(false);
  // Récurrence (RecurrenceEditor) : quand générer + fin ; indice affiché
  const [rec, setRec] = useState({ frequency: "weekly", weekdays: [0], day_of_month: 1, month_of_year: null, time: "09:00", custom_date: "", endMode: "never", end_date: "", max_occurrences: null });
  const [idx, setIdx] = useState({ type: "", start: "", values: [], rawValues: "" });
  const [submitting, setSubmitting] = useState(false);

  // Preview of the next reference (e.g. "TH0044/26") + live-ticking creation
  // clock, both shown read-only at the top of the dialog. The reference is
  // fetched every time the dialog opens so we always show the freshest peek.
  const [previewRef, setPreviewRef] = useState("");
  const [nowTs, setNowTs] = useState(() => new Date());

  useEffect(() => {
    if (!open) return;
    // Edit mode : pre-fill EVERY field from existingTask. New reference
    // preview is skipped (we show the existing reference instead).
    if (isEditMode) {
      const t = existingTask;
      setSubject(t.subject || "");
      setMessage(t.message || "");
      const pName = projectList.find((p) => p.id === t.project_id)?.name || "";
      setProjectName(pName);
      setExecutionDate(t.execution_date ? t.execution_date.slice(0, 16) : "");
      setDeadline(t.deadline ? t.deadline.slice(0, 16) : "");
      setPriority(t.priority || "moyen");
      setAssignees((t.assignments || []).map((a) => ({ user_id: a.user_id, role: a.role || "executor" })));
      setChecklist((t.checklist || []).map((c) => c.label));
      setReminders((t.reminders || []).map((r) => ({
        id: r.id, at: r.at, type: r.type || "before", interval_minutes: r.interval_minutes,
      })));
      // Recurrence
      const rec = t.recurrence;
      if (rec && rec.active) {
        setRecEnabled(true);
        setRec({ frequency: rec.frequency === "hourly" ? "daily" : rec.frequency || "weekly", weekdays: rec.weekdays?.length ? rec.weekdays : [0], day_of_month: rec.day_of_month || 1, month_of_year: rec.month_of_year || null, time: rec.time || "09:00", custom_date: rec.custom_date ? rec.custom_date.slice(0, 16) : "", endMode: rec.end_date ? "date" : rec.max_occurrences ? "count" : "never", end_date: rec.end_date || "", max_occurrences: rec.max_occurrences || null });
        setIdx({ type: rec.index_type || "", start: rec.index_start || "", values: rec.index_values || [], rawValues: (rec.index_values || []).join("\n") });
      }
      setPreviewRef(t.reference || "");
      setNowTs(new Date(t.created_at || Date.now()));
      // Pré-remplir le lien AVI (édition).
      return;   // don't fetch next-reference in edit mode
    }
    // Create mode
    if (initialExecutionDate) setExecutionDate(initialExecutionDate);
    // Spec 2026-02-XX rev16 (point J) — pré-remplissage depuis une fiche
    // document. `initialSubject` = "[Type] — [Titre]" ; `initialProjectId`
    // = siège du document ; `initialChecklist` = suggestions issues du
    // workflow (étapes en cours / en retard).
    if (initialSubject) setSubject(initialSubject);
    if (initialMessage) setMessage(initialMessage);
    if (initialChecklist && initialChecklist.length > 0) {
      // Le state ``checklist`` stocke des STRINGS (le sujet du checkbox).
      setChecklist(initialChecklist.filter(Boolean).map(String));
    }
    if (initialProjectId && Array.isArray(projectList)) {
      const p = projectList.find((x) => x.id === initialProjectId);
      if (p) setProjectName(p.name);
    }
    let cancelled = false;
    api.get("/tasks/next-reference")
      .then(({ data }) => { if (!cancelled) setPreviewRef(data.reference || ""); })
      .catch(() => { if (!cancelled) setPreviewRef(""); });
    // Tick the clock every second so the "Créée le" line stays alive while
    // the user fills the form.
    setNowTs(new Date());
    const id = setInterval(() => setNowTs(new Date()), 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, initialExecutionDate, isEditMode, existingTask?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setSubject(""); setMessage("");
      setProjectName("");
      setExecutionDate(""); setDeadline(""); setPriority("moyen");
      setAssignees([]); setChecklist([]); setCheckInput("");
      setReminders([]); setRemType("before"); setDuration({ ...EMPTY_DURATION });
      attachQueue.reset();
      setRecEnabled(false);
      setRec({ frequency: "weekly", weekdays: [0], day_of_month: 1, month_of_year: null, time: "09:00", custom_date: "", endMode: "never", end_date: "", max_occurrences: null });
      setIdx({ type: "", start: "", values: [], rawValues: "" });
      setSubmitting(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const projectId = useMemo(
    () => projectList.find((p) => p.name === projectName)?.id || "",
    [projectList, projectName]
  );

  const canSubmit = useMemo(
    () => subject.trim() && !submitting,
    [subject, submitting]
  );
  const req = useRequiredFields([{ key: "subject", label: "Objet", ok: !!subject.trim() }]);

  const addChecklist = () => {
    const v = checkInput.trim();
    if (!v) return;
    setChecklist((c) => [...c, v]);
    setCheckInput("");
  };
  const removeChecklist = (i) => setChecklist((c) => c.filter((_, idx) => idx !== i));

  // Spec iter203 (2026-02-28) — édition inline + drag-and-drop
  // natif pour la Checklist. Le state ``checklist`` reste un tableau
  // de STRINGS (contrat API inchangé) ; les fonctions helper opèrent
  // toutes sur des index.
  const updateChecklist = (i, v) => {
    setChecklist((c) => c.map((s, idx) => (idx === i ? v : s)));
  };
  const moveChecklist = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    setChecklist((c) => {
      const next = [...c];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  // ------- Reminders ------------------------------------------------------
  const reminderRef = useMemo(() => {
    // Anchor = deadline first (that's when the "1 day before" is most useful),
    // fall back to execution_date. Both empty → presets disabled.
    // parseDateOrNull : une saisie datetime-local invalide (ex. année à
    // 5 chiffres) donnait une Date invalide → toISOString() plantait.
    return parseDateOrNull(deadline) || parseDateOrNull(executionDate);
  }, [deadline, executionDate]);

  const _pushReminder = (entry) => {
    // Dedupe on (at + type + interval) so double-click can't duplicate.
    const key = `${entry.at}::${entry.type}::${entry.interval_minutes || ""}`;
    setReminders((prev) =>
      prev.some((r) => `${r.at}::${r.type}::${r.interval_minutes || ""}` === key)
        ? prev
        : [...prev, { id: `${key}-${Math.random().toString(36).slice(2, 7)}`, ...entry }]
            .sort((a, b) => (a.at < b.at ? -1 : 1))
    );
  };
  const removeReminder = (id) => setReminders((prev) => prev.filter((r) => r.id !== id));

  // Unified add — uses the 6-counter DurationPicker for all 3 flavours.
  //   • Avant   : one-shot at (anchor - duration).
  //   • Pendant : repeating from now, at `duration` cadence (until completed).
  //   • Après   : repeating from anchor, at `duration` cadence (until completed).
  const addReminder = () => {
    const totalMin = toMinutes(duration);
    if (!totalMin) {
      toast.warning("Sélectionnez une durée non nulle");
      return;
    }
    if (remType === "before") {
      if (!reminderRef) {
        toast.warning("Renseignez d'abord une deadline ou une date d'exécution");
        return;
      }
      const at = new Date(reminderRef.getTime() - totalMin * 60 * 1000);
      if (at.getTime() < Date.now()) {
        toast.warning("Ce rappel serait déjà passé");
        return;
      }
      _pushReminder({ at: at.toISOString(), type: "before" });
    } else if (remType === "during") {
      // Repeats from NOW until the task is marked completed
      _pushReminder({
        at: new Date().toISOString(),
        type: "during",
        interval_minutes: totalMin,
      });
    } else if (remType === "after") {
      if (!reminderRef) {
        toast.warning("Renseignez d'abord une deadline ou une date d'exécution");
        return;
      }
      // Only one "after" reminder allowed — new one replaces the previous.
      setReminders((prev) => {
        const withoutAfter = prev.filter((r) => r.type !== "after");
        return [...withoutAfter, {
          id: `after-${Math.random().toString(36).slice(2, 8)}`,
          at: reminderRef.toISOString(),
          type: "after",
          interval_minutes: totalMin,
        }].sort((a, b) => (a.at < b.at ? -1 : 1));
      });
    }
    setDuration({ ...EMPTY_DURATION });
  };

  const fmtReminder = (iso) =>
    new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const submit = async () => {
    if (submitting || !req.validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        subject: subject.trim(),
        message: message.trim(),
        project_id: projectId || null,
        execution_date: toIsoOrNull(executionDate),
        deadline: toIsoOrNull(deadline),
        priority,
        assignees: assignees.map((a) => ({ user_id: a.user_id, role: a.role })),
        checklist,
        doc_link: null,
        // Spec 2026-02-XX rev16 (point J) — liaison document (uniquement
        // à la CRÉATION ; en édition, la liaison reste immutable pour
        // préserver l'intégrité du contrôle de clôture matrix-based).
        ...(linkedDocumentId && !isEditMode ? { linked_document_id: linkedDocumentId } : {}),
        ...(sourceAviId && !isEditMode ? { source_avi_id: sourceAviId } : {}),
        // Spec iter105 (Feb 2026) — liaison AVI facultative, éditable
        // dans les 2 modes (create + edit). "" = déliaison, valeur ⇒
        // linkage. On envoie toujours le champ pour permettre la
        // déliaison depuis le formulaire d'édition.
        reminders: reminders.map((r) => ({
          at: r.at,
          type: r.type,
          ...(r.interval_minutes ? { interval_minutes: r.interval_minutes } : {}),
        })),
        recurrence: recEnabled
          ? {
              active: true,
              frequency: rec.frequency,
              custom_hours: null,
              weekdays: rec.frequency === "weekly" ? rec.weekdays : [],
              day_of_month: ["monthly", "quarterly", "semiannual", "yearly"].includes(rec.frequency) ? Number(rec.day_of_month) || null : null,
              month_of_year: ["quarterly", "semiannual", "yearly"].includes(rec.frequency) ? Number(rec.month_of_year) || null : null,
              time: rec.time || null,
              custom_date: rec.frequency === "custom" && rec.custom_date ? new Date(rec.custom_date).toISOString() : null,
              end_date: rec.endMode === "date" && rec.end_date ? rec.end_date : null,
              max_occurrences: rec.endMode === "count" ? Number(rec.max_occurrences) || null : null,
              trigger_rule: "on_calendar",
              if_previous_open: "create_anyway",
              index_type: idx.type || null,
              index_start: idx.type === "month" || idx.type === "year" ? String(idx.start || "") || null : null,
              index_values: idx.type === "custom" ? idx.values : [],
            }
          : null,
      };
      let data;
      if (isEditMode) {
        ({ data } = await api.patch(`/tasks/${existingTask.id}`, payload));
      } else {
        ({ data } = await api.post("/tasks", payload));
      }
      // Upload every queued attachment with live progression on the same
      // FileOrScanInput cards the user already sees. If any upload fails or
      // is canceled we surface a toast but still close the dialog once the
      // batch completes — the task itself was created OK.
      await attachQueue.uploadAll(async (file, { onProgress, signal }) => {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/tasks/${data.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => onProgress?.((e.loaded / (e.total || file.size)) * 100, e.loaded),
          signal,
        });
      });
      toast.success(isEditMode ? "Tâche modifiée" : "Tâche créée");
      onOpenChange(false);
      if (isEditMode) onUpdated?.(data);
      else onCreated?.(data);
    } catch (err) {
      toast.error(isEditMode ? "Impossible de modifier la tâche" : "Impossible de créer la tâche", { description: errText(err, "") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModuleDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEditMode ? "Modifier la tâche" : "Nouvelle tâche"}
      description={isEditMode ? "Toutes les valeurs actuelles sont pré-remplies et modifiables." : "Créer une tâche pour votre équipe."}
      moduleColor={MODULE_COLORS.tasks}
      moduleIcon={<ListTodo size={22} />}
      testId="task-create-dialog"
      stickyContent={
        /* Spec iter181 (2026-02-27) — bandeau RÉFÉRENCE/CRÉÉE LE déplacé
           dans `stickyContent` de `ModuleDialog` pour rester figé
           pendant le scroll du corps de la modale (aligné sur le
           comportement des modales AVI). */
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-md px-3 py-2"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
          data-testid="tc-readonly-header"
        >
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Référence</span>
            <span
              className="inline-block text-[12px] font-bold px-2 py-0.5 rounded tabular-nums"
              style={{ background: YELLOW, color: "#1B1B1B" }}
              data-testid="tc-preview-reference"
            >
              {previewRef || "…"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarIcon size={12} className="text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Créée le</span>
            <span
              className="text-[12px] font-medium tabular-nums"
              data-testid="tc-preview-now"
            >
              {nowTs.toLocaleString("fr-FR", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit", second: "2-digit",
              })}
            </span>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end items-center gap-2">
          <MissingSummary errors={req.errors} testId="tc-missing-hint" className="mr-auto" />
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tc-cancel">Annuler</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-incomplete={!canSubmit || undefined}
            className="text-white font-medium hover:brightness-110"
            style={{ background: "#D99B1E" }}
            data-testid="tc-submit"
          >
            {submitting
              ? (isEditMode ? "Enregistrement…" : "Création…")
              : (isEditMode ? "Enregistrer" : "Créer la tâche")}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Spec iter181 (2026-02-27) — le bloc RÉFÉRENCE/CRÉÉE LE
            précédemment ici a été déplacé dans la prop `stickyContent`
            de ModuleDialog pour rester figé pendant le scroll. */}
        {/* --------------------------- Informations ---------------------- */}
          <FormSection title="Informations" icon={<FileText size={16} />} iconColor={YELLOW} testIdPrefix="tc-section">
            <div className="space-y-4">
              <div data-field="subject">
                <Label htmlFor="tc-subject" className="mb-1.5 block">Objet<Req /></Label>
                <Input
                  id="tc-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                  placeholder="Titre court de la tâche" data-testid="tc-subject" autoFocus className={requiredClass(req.has("subject"))}
                />
                <FieldError show={req.has("subject")} testId="tc-subject-error" />
              </div>
              <div>
                <Label htmlFor="tc-message" className="mb-1.5 block">Description</Label>
                <Textarea
                  id="tc-message" value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder="Décrivez la tâche en détail…" rows={4} data-testid="tc-message"
                />
              </div>
            </div>
          </FormSection>

          {/* --------------------------- Assignation ----------------------- */}
          <FormSection title="Assignation" icon={<Users size={16} />} iconColor={YELLOW} testIdPrefix="tc-section">
            <AssigneeSelector users={users} value={assignees} onChange={setAssignees} />
          </FormSection>

          {/* --------------------------- Planification --------------------- */}
          <FormSection title="Planification" icon={<CalendarIcon size={16} />} iconColor={YELLOW} testIdPrefix="tc-section">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="mb-1.5 block">Date d&apos;exécution</Label>
                <Input type="datetime-local" value={executionDate} onChange={(e) => setExecutionDate(e.target.value)} data-testid="tc-execution" />
              </div>
              <div>
                <Label className="mb-1.5 block">Deadline</Label>
                <Input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="tc-deadline" />
                {/* Spec rev152 (2026-02-07) — aperçu live du badge palette rev150. */}
                {toIsoOrNull(deadline) && (
                  <div className="mt-2" data-testid="tc-deadline-preview">
                    <DeadlineInline iso={toIsoOrNull(deadline)} size={22} />
                  </div>
                )}
              </div>
              <div>
                <Label className="mb-1.5 block">Priorité</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="tc-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`tc-priority-opt-${p}`}>
                        <div className="flex items-center gap-2"><PriorityBadge priority={p} /></div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* --- Custom reminders --------------------------------------- */}
            <div className="mt-5 pt-4 border-t border-vertec-borderDark" data-testid="tc-reminders">
              <div className="flex items-center gap-2 mb-2">
                <Bell size={14} style={{ color: YELLOW }} />
                <Label className="text-sm">Rappel personnalisé</Label>
                <span className="text-[11px] text-muted-foreground">
                  {reminderRef
                    ? "Calculé par rapport à la deadline (ou date d'exécution)"
                    : "Renseignez une deadline ou date d'exécution pour activer les raccourcis"}
                </span>
              </div>

              {/* Type tabs: Avant / Pendant / Après ---------------------- */}
              <div className="inline-flex items-center gap-1 p-1 bg-vertec-borderDark/40 rounded-lg mb-3" role="tablist" data-testid="tc-reminder-type-tabs">
                {["before", "during", "after"].map((k) => {
                  const meta = REMINDER_META[k];
                  const active = remType === k;
                  const IconT = meta.Icon;
                  return (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setRemType(k)}
                      data-testid={`tc-reminder-tab-${k}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                        active ? "text-white shadow-sm" : "text-muted-foreground hover:text-white"
                      }`}
                      style={active ? { background: meta.color } : undefined}
                    >
                      <IconT size={12} />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground italic mb-3">
                {REMINDER_META[remType].hint}
              </p>

              {/* --- Unified 6-counter duration picker for all 3 flavours --- */}
              <div className="p-3 rounded-md border border-[#3A3A3A] bg-white/[0.02] mb-3">
                <DurationPicker
                  value={duration}
                  onChange={setDuration}
                  accent={REMINDER_META[remType].color}
                  testIdPrefix={`tc-dur-${remType}`}
                />
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[11px] text-muted-foreground">
                    {toMinutes(duration) > 0
                      ? <>Sélection : <span className="text-white font-medium">{formatDuration(duration)}</span></>
                      : "Ajustez les compteurs pour définir la durée."}
                  </span>
                  <Button
                    type="button"
                    onClick={addReminder}
                    disabled={toMinutes(duration) === 0 || (remType !== "during" && !reminderRef)}
                    style={{ background: REMINDER_META[remType].color, color: "#FFFFFF" }}
                    className="hover:opacity-90 border-0 gap-1.5"
                    data-testid={`tc-dur-${remType}-add`}
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    Ajouter le rappel
                  </Button>
                </div>
              </div>

              {/* --- List of programmed reminders (mixed types) --------- */}
              {reminders.length > 0 && (
                <ul className="space-y-1.5 mt-3" data-testid="tc-reminder-list">
                  {reminders.map((r) => {
                    const meta = REMINDER_META[r.type] || REMINDER_META.before;
                    const IconT = meta.Icon;
                    return (
                      <li
                        key={r.id}
                        className="flex items-center gap-2 rounded px-2.5 py-1.5 text-sm"
                        style={{ borderLeft: `3px solid ${meta.color}`, border: `1px solid ${meta.color}55`, borderLeftWidth: "3px" }}
                        data-testid={`tc-reminder-item-${r.type}`}
                      >
                        <IconT size={12} style={{ color: meta.color }} />
                        <span
                          className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: `${meta.color}22`, color: meta.color }}
                        >
                          {meta.label}
                        </span>
                        <span className="flex-1 tabular-nums">
                          {fmtReminder(r.at)}
                          {(r.type === "after" || r.type === "during") && r.interval_minutes && (
                            <span className="text-muted-foreground text-xs">
                              {" "}· puis toutes les {
                                r.interval_minutes < 60
                                  ? `${r.interval_minutes} min`
                                  : r.interval_minutes % 60 === 0 && r.interval_minutes < 1440
                                    ? `${r.interval_minutes / 60} h`
                                    : r.interval_minutes % 1440 === 0
                                      ? `${r.interval_minutes / 1440} j`
                                      : `${r.interval_minutes} min`
                              }
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeReminder(r.id)}
                          className="text-muted-foreground hover:text-status-danger"
                          aria-label="Retirer"
                          data-testid={`tc-reminder-remove-${r.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {reminders.length === 0 && (
                <p className="text-[11px] text-muted-foreground italic">
                  Aucun rappel programmé. Chaque rappel enverra une notification plateforme + e-mail à tous les assignés.
                </p>
              )}
            </div>
          </FormSection>

          {/* --------------------------- Siège / Projet --------------------- */}
          {/* "Siège / Projet" (was "Destination / Projet") is the only field
              exposed at creation. The underlying data model still uses
              `project_id` — we simply relabel it in the UI so it matches
              Vertec's business vocabulary (a real-estate site / location
              rather than a project). The Département field was dropped at
              Vertec's request. */}
          <FormSection title="Siège / Projet" icon={<Target size={16} />} iconColor={YELLOW} testIdPrefix="tc-section">
            <div>
              <Combobox
                endpoint="/projects"
                value={projectName}
                onChange={(v) => setProjectName(v || "")}
                onCreated={(item) => setProjectList((prev) => [...prev, item])}
                canCreate={isAdmin}
                canDelete={isAdmin}
                placeholder="Choisir un siège / projet…"
                showClear={true}
                testid="tc-project"
              />
              {!isAdmin && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Seul un administrateur peut créer un nouveau siège / projet.
                </p>
              )}
            </div>
          </FormSection>

          {/* --------------------------- Checklist ------------------------- */}
          <FormSection title="Checklist" icon={<ListChecks size={16} />} iconColor={YELLOW} testIdPrefix="tc-section">
            <div className="flex gap-2 mb-2">
              <Input
                value={checkInput}
                onChange={(e) => setCheckInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChecklist(); } }}
                placeholder="Ajouter une étape et Entrée"
                data-testid="tc-checklist-input"
              />
              <Button
                type="button"
                onClick={addChecklist}
                style={{ background: PLUS_GREEN, color: "#FFFFFF" }}
                className="hover:opacity-90 border-0"
                data-testid="tc-checklist-add"
                aria-label="Ajouter"
              >
                <Plus size={14} />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2" />
            {/* Spec iter203 (2026-02-28) — Liste verticale à la place
                des chips côte à côte : bloc gris arrondi #1e1e20, une
                étape par ligne, poignée de drag à gauche (HTML5 DnD
                natif — pas de dep), texte éditable inline (clic ou
                icône crayon), corbeille pour supprimer. Le champ
                « Ajouter une étape » reste au-dessus. */}
            {checklist.length > 0 && (
              <div
                className="mt-1 rounded-lg p-1 space-y-1"
                style={{ background: "#1e1e20" }}
                data-testid="tc-checklist-list"
              >
                {checklist.map((c, i) => (
                  <ChecklistRow
                    key={i}
                    index={i}
                    value={c}
                    onChange={(v) => updateChecklist(i, v)}
                    onRemove={() => removeChecklist(i)}
                    onMove={moveChecklist}
                  />
                ))}
              </div>
            )}
          </FormSection>

          {/* --------------------------- Pièces jointes -------------------- */}
          <FormSection title="Pièces jointes" icon={<Paperclip size={16} />} iconColor={YELLOW} testIdPrefix="tc-section">
            <FileOrScanInput
              entries={attachQueue.entries}
              onFiles={attachQueue.enqueue}
              onCancel={attachQueue.cancel}
              onRemove={attachQueue.remove}
              multiple
              testIdPrefix="tc-attach"
            />
          </FormSection>

          {/* --------------------------- Récurrence ------------------------ */}
          <div data-testid="tc-section-recurrence">
            <RecurrenceEditor enabled={recEnabled} onEnabled={setRecEnabled} rec={rec} setRec={setRec} idx={idx} setIdx={setIdx} subject={subject} anchorDate={executionDate || deadline || null} />
          </div>
        </div>
    </ModuleDialog>
  );
}


// ---------------------------------------------------------------------------
// AddByLocation — shortcut button + popover that resolves every user whose
// default_project (Siège / Projet par défaut) matches the picked project and
// bulk-adds them to the assignee list. Purely a saisie shortcut: nothing is
// stored under a Location concept — only the individual `assignees` are.
// Spec 2026-02-15 (Lot B · point 5).
// ---------------------------------------------------------------------------
function AddByLocation({ projects, users, onAdd }) {
  const [open, setOpen] = useState(false);
  const pick = (proj) => {
    const matching = (users || []).filter(
      (u) => (u.default_project || "").trim().toLowerCase() === (proj.name || "").trim().toLowerCase()
    );
    if (matching.length === 0) {
      toast.info(`Aucun utilisateur avec la siège / projet « ${proj.name} » par défaut`);
      setOpen(false);
      return;
    }
    onAdd(matching);
    toast.success(`+${matching.length} personne${matching.length > 1 ? "s" : ""} ajoutée${matching.length > 1 ? "s" : ""} depuis ${proj.name}`);
    setOpen(false);
  };
  return (
    <div className="relative mt-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        data-testid="tc-add-by-location"
        className="gap-2 border-vertec-blue text-vertec-blue hover:bg-vertec-blue/10"
      >
        <MapPin size={14} /> Ajouter par siège / projet
        <ChevronDown size={12} className="opacity-70" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 mt-1 z-40"
            style={{ ...PANEL, minWidth: 280, maxHeight: 320, overflowY: "auto" }}
            data-testid="tc-add-by-location-popover"
          >
            <J3Groups projects={projects || []} onPick={pick} testIdPrefix="tc-add-by-location" rightOf={(p) => {
              const count = (users || []).filter((u) => (u.default_project || "").trim().toLowerCase() === (p.name || "").trim().toLowerCase()).length;
              return <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 tabular-nums" style={{ background: count > 0 ? "#0078D426" : "#3A3A3A", color: count > 0 ? "#4FA8E0" : "#9CA3AF" }}>{count}</span>;
            }} />
          </div>
        </>
      )}
    </div>
  );
}


/**
 * ChecklistRow — Spec iter203 (2026-02-28).
 * Ligne d'étape de checklist : poignée grip (drag), texte éditable
 * inline (click ou icône crayon), corbeille pour supprimer.
 * Drag-and-drop HTML5 natif (léger, pas de dépendance externe).
 */
function ChecklistRow({ index, value, onChange, onRemove, onMove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [dragOver, setDragOver] = useState(false);

  const commit = () => {
    const v = draft.trim();
    if (!v) { setDraft(value); setEditing(false); return; }
    if (v !== value) onChange(v);
    setEditing(false);
  };
  const cancel = () => { setDraft(value); setEditing(false); };

  return (
    <div
      draggable={!editing}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(index));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const from = Number(e.dataTransfer.getData("text/plain"));
        if (!Number.isNaN(from)) onMove(from, index);
      }}
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors"
      style={{
        background: dragOver ? "rgba(249,188,38,0.10)" : "transparent",
        border: dragOver ? "1px dashed rgba(249,188,38,0.4)" : "1px solid transparent",
        cursor: editing ? "default" : "grab",
      }}
      data-testid={`tc-checklist-row-${index}`}
    >
      <span
        className="flex-shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100"
        aria-hidden="true"
        title="Glisser pour réordonner"
      >
        <GripVertical size={14} />
      </span>
      {editing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          onBlur={commit}
          className="flex-1 h-7 text-sm"
          data-testid={`tc-checklist-input-edit-${index}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-1 text-left text-sm truncate hover:text-white transition-colors"
          title="Cliquer pour modifier"
          data-testid={`tc-checklist-text-${index}`}
        >
          {value}
        </button>
      )}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-white"
        aria-label="Modifier"
        title="Modifier"
        data-testid={`tc-checklist-edit-${index}`}
      >
        <Pencil size={13} />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-status-danger"
        aria-label="Supprimer"
        title="Supprimer"
        data-testid={`tc-checklist-remove-${index}`}
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}
